import { useEffect, type ReactNode } from "react";

import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { cn } from "@/lib/classNames";

/** Matches map naming and other board dialogs — use for all `h2` titles inside the shell. */
export const MONOLITH_BOARD_MODAL_TITLE_CLASS =
  "text-xl font-black uppercase tracking-[-0.04em] text-neutral-950";

export const MONOLITH_BOARD_MODAL_EYEBROW_CLASS =
  "text-[10px] uppercase tracking-[0.28em] text-neutral-600";

export const MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS =
  "mb-2 block text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-700";

export const MONOLITH_BOARD_MODAL_INPUT_CLASS =
  "w-full border-[3px] border-neutral-950 bg-white px-4 py-3 text-base font-semibold text-neutral-950 outline-none transition focus:border-[#3e664d]";

export const MONOLITH_BOARD_MODAL_SECTION_DIVIDER_CLASS = "mt-6 border-t-2 border-neutral-950/12 pt-6";

type MonolithBoardModalProps = {
  open: boolean;
  titleId: string;
  onClose: () => void;
  submitting?: boolean;
  /** When false, backdrop clicks and Escape do not close (e.g. blocking setup). */
  dismissible?: boolean;
  /** Appended after base padding and border. When omitted, the panel uses `max-w-lg`; when set, include any `max-w-*` you need (defaults do not merge). */
  panelClassName?: string;
  children: ReactNode;
};

/**
 * Shared backdrop + panel for editor/dashboard “monolith board” dialogs (create map, sign-in flow, etc.).
 */
export function MonolithBoardModal({
  open,
  titleId,
  onClose,
  submitting = false,
  dismissible = true,
  panelClassName,
  children,
}: MonolithBoardModalProps) {
  useEffect(() => {
    if (!open || !dismissible) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !submitting) {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dismissible, onClose, open, submitting]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-140 flex items-center justify-center bg-[rgba(10,10,10,0.52)] px-4 py-8"
      role="presentation"
      onMouseDown={(event) => {
        if (!dismissible) {
          return;
        }

        if (event.target === event.currentTarget && !submitting) {
          onClose();
        }
      }}
    >
      <article
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "flex w-full flex-col p-6 sm:p-8",
          MONOLITH_BOARD_STYLE.panelClassName,
          panelClassName,
          // `cn` does not merge Tailwind conflicts; omit default max-width when callers set their own.
          !panelClassName && "max-w-lg",
        )}
      >
        {children}
      </article>
    </div>
  );
}

export type MonolithBoardModalStep = {
  id: string;
  label: string;
  status: "complete" | "current" | "upcoming";
  onSelect?: () => void;
};

type MonolithBoardModalStepsProps = {
  steps: MonolithBoardModalStep[];
  stepNumber: number;
  stepCount: number;
  currentStepLabel: string;
  disabled?: boolean;
  className?: string;
};

/** Optional step switcher for multi-step flows inside {@link MonolithBoardModal}. */
export function MonolithBoardModalSteps({
  steps,
  stepNumber,
  stepCount,
  currentStepLabel,
  disabled = false,
  className,
}: MonolithBoardModalStepsProps) {
  return (
    <div
      className={cn(className)}
      aria-label={`Step ${stepNumber} of ${stepCount}: ${currentStepLabel}`}
    >
      <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS}>Steps</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {steps.map((step) => (
          <button
            key={step.id}
            type="button"
            disabled={step.status === "upcoming" || disabled}
            onClick={() => {
              if (step.status === "complete" && !disabled) {
                step.onSelect?.();
              }
            }}
            className={cn(
              "border-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors",
              step.status === "current" && MONOLITH_BOARD_STYLE.switcherActiveClassName,
              step.status === "complete" &&
                cn(MONOLITH_BOARD_STYLE.switcherIdleClassName, "cursor-pointer bg-[#fff2d9] hover:bg-[#ffe7bc]"),
              step.status === "upcoming" &&
                cn(MONOLITH_BOARD_STYLE.switcherIdleClassName, "cursor-not-allowed opacity-45"),
            )}
          >
            {step.label}
          </button>
        ))}
      </div>
    </div>
  );
}
